import { useEffect, useMemo, useState } from "react";
import {
  Plus, Search, SlidersHorizontal, Pencil, Eye, Archive, RotateCcw, Trash2,
  Sparkles, Star, Paperclip, FileText, Check, X, Briefcase, Target, Loader2,
  MessageSquare,
} from "lucide-react";
import {
  useFreelancerJobRecordsStore,
  type JobRecordRow,
} from "../store/useFreelancerJobRecordsStore";
import { useAuthStore } from "../store/useAuthStore";
import JobRecordModal from "../components/freelancer/JobRecordModal";

/**
 * Freelancer Job Records — an account's completed work, kept as evidence.
 *
 * The page is account-scoped from the first render: the account picker is the
 * first thing on it, and nothing below it renders without one. That is not a
 * layout choice — history from two freelance accounts must never appear in one
 * table, because the whole value of this data is being able to say "this account
 * has done this before" and mean it.
 */

const money = (n: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return `${currency} ${(n || 0).toLocaleString()}`;
  }
};

/** A statistic that refuses to invent a number it does not have. */
const StatCard = ({
  icon,
  label,
  value,
  sub,
  enough,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  enough: boolean;
}) => (
  <div className="p-4 rounded-xl bg-surface-container-lowest border border-outline-variant shadow-sm">
    <div className="flex items-center gap-2 mb-2 text-on-surface-variant">
      {icon}
      <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
    </div>
    {enough ? (
      <>
        <p className="text-2xl font-black text-on-surface leading-tight">{value}</p>
        {sub && <p className="text-[11px] text-on-surface-variant mt-0.5">{sub}</p>}
      </>
    ) : (
      <p className="text-sm font-semibold text-outline mt-1">Not enough data</p>
    )}
  </div>
);

/** Inline edit for one simple field. Complex information stays in the modal. */
const InlineEdit = ({
  value,
  onSave,
  type = "text",
  prefix = "",
  width = "w-28",
}: {
  value: string | number | null;
  onSave: (v: string) => Promise<boolean>;
  type?: "text" | "number";
  prefix?: string;
  width?: string;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(String(value ?? "")), [value]);

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="group inline-flex items-center gap-1 text-left hover:text-primary transition-colors"
        title="Click to edit"
      >
        <span>{value === null || value === "" ? "—" : `${prefix}${value}`}</span>
        <Pencil size={11} className="opacity-0 group-hover:opacity-60 shrink-0" />
      </button>
    );
  }

  const commit = async () => {
    setBusy(true);
    const ok = await onSave(draft);
    setBusy(false);
    if (ok) setEditing(false);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className={`${width} px-2 py-1 bg-surface-container-low border border-primary rounded text-xs outline-none`}
      />
      <button onClick={commit} disabled={busy} className="p-1 text-tertiary hover:bg-surface-container-low rounded">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
      </button>
      <button onClick={() => setEditing(false)} className="p-1 text-outline hover:bg-surface-container-low rounded">
        <X size={12} />
      </button>
    </span>
  );
};

/**
 * Describe a job, see which past work supports it and why.
 *
 * This is the knowledge layer made visible. It exists because a ranking nobody
 * can inspect is a ranking nobody will trust — and because it is the clearest
 * way to show what these records are actually for.
 */
const EvidenceTester = ({ onClose }: { onClose: () => void }) => {
  const findEvidence = useFreelancerJobRecordsStore((s) => s.findEvidence);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [skills, setSkills] = useState("");
  const [matches, setMatches] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    const result = await findEvidence({
      title,
      description,
      skills: skills.split(",").map((s) => s.trim()).filter(Boolean),
    });
    setBusy(false);
    setMatches(result || []);
  };

  const input =
    "w-full px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div className="mb-lg p-4 rounded-xl bg-surface-container-lowest border border-primary/30">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
          <Target size={15} className="text-primary" />
          Which past work supports this job?
        </h3>
        <button onClick={onClose} className="p-1 text-on-surface-variant hover:text-on-surface">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <input className={input} placeholder="Job title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className={`${input} sm:col-span-2`} placeholder="Required skills, comma separated" value={skills} onChange={(e) => setSkills(e.target.value)} />
      </div>
      <textarea className={`${input} min-h-[80px] resize-y mb-3`} placeholder="Paste the job description" value={description} onChange={(e) => setDescription(e.target.value)} />

      <button onClick={run} disabled={busy} className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
        {busy && <Loader2 size={14} className="animate-spin" />}
        Find supporting work
      </button>

      {matches !== null && (
        <div className="mt-4">
          {matches.length === 0 ? (
            <p className="text-sm text-on-surface-variant">
              Nothing in this account's records supports that job. That is a useful answer — bidding
              on it would mean doing so without evidence.
            </p>
          ) : (
            <div className="space-y-2">
              {matches.map((m, i) => (
                <div key={i} className="p-3 rounded-lg border border-outline-variant">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <span className="text-sm font-bold text-on-surface">{m.evidence.jobTitle}</span>
                    <span className="px-2 py-0.5 rounded-full bg-tertiary-container text-tertiary text-xs font-bold shrink-0">
                      {m.score}
                    </span>
                  </div>
                  {/* The reasons, not just the number. */}
                  <ul className="text-[11px] text-on-surface-variant space-y-0.5">
                    {m.reasons.map((r: string, j: number) => (
                      <li key={j}>· {r}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FreelancerJobRecordsPage = () => {
  const {
    accounts, accountId, records, total, stats, options, filters, isLoading,
    fetchAccounts, fetchRecords, selectAccount, setFilters, resetFilters,
    patchField, archiveRecord, restoreRecord, deleteRecord,
  } = useFreelancerJobRecordsStore();

  const authUser = useAuthStore((s) => s.authUser);
  const isAdmin = authUser?.role === "Admin";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showTester, setShowTester] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [confirmArchive, setConfirmArchive] = useState<JobRecordRow | null>(null);

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced, so the table does not refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== filters.search) setFilters({ search: searchDraft });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  useEffect(() => {
    if (accountId) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const account = useMemo(() => accounts.find((a) => a.id === accountId), [accounts, accountId]);

  const activeFilterCount = [
    filters.category, filters.subcategory, filters.contractType,
    filters.rating, filters.minValue, filters.maxValue, filters.from, filters.to, filters.skill,
  ].filter(Boolean).length;

  const open = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  return (
    // pt-20 clears the fixed top bar, as every other full-page screen does.
    // Without it the account context header scrolls underneath it, which is the
    // one line on this page that must always be visible — it names whose records
    // are on screen.
    <div className="min-h-screen bg-background">
      <div className="max-w-[1500px] mx-auto px-4 lg:px-6 pt-20 pb-10">
      {/* ── Account context ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-lg">
        <div className="flex items-center gap-3 min-w-0">
          {account?.profilePicUrl ? (
            <img src={account.profilePicUrl} alt="" className="w-11 h-11 rounded-full object-cover" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center">
              <Briefcase size={18} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-black text-on-surface">Freelancer Job Records</h1>
            <p className="text-sm text-on-surface-variant">
              {account ? (
                <>
                  {/* The account name is only worth repeating when it differs
                      from the display name — otherwise it reads "Farah · Farah". */}
                  {[account.name, account.platform, account.accountName !== account.name ? account.accountName : ""]
                    .filter(Boolean)
                    .join(" · ")}
                  <span className="text-on-surface-variant">
                    {" · "}
                    {account.recordCount} record{account.recordCount === 1 ? "" : "s"}
                  </span>
                </>
              ) : (
                "Select a freelance account"
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={accountId}
            onChange={(e) => selectAccount(e.target.value)}
            className="px-3 py-2 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary"
          >
            {accounts.length === 0 && <option value="">No accounts available</option>}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} — {a.platform} ({a.recordCount})
              </option>
            ))}
          </select>

          <button
            onClick={() => setShowTester((v) => !v)}
            className="px-3 py-2 rounded-lg border border-outline-variant text-sm font-semibold text-on-surface hover:bg-surface-container-low flex items-center gap-1.5"
          >
            <Target size={15} /> Test matching
          </button>

          <button
            onClick={() => open(null)}
            disabled={!accountId}
            className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            <Plus size={16} /> Add Job Record
          </button>

          <button
            disabled
            title="Bulk import is not built yet"
            className="px-3 py-2 rounded-lg border border-outline-variant text-sm font-semibold text-outline cursor-not-allowed"
          >
            Import
          </button>
        </div>
      </div>

      {!accountId ? (
        <p className="text-sm text-on-surface-variant py-12 text-center">
          Choose a freelance account to see its job records.
        </p>
      ) : (
        <>
          {showTester && <EvidenceTester onClose={() => setShowTester(false)} />}

          {/* ── The four statistics ───────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-lg">
            <StatCard
              icon={<Briefcase size={14} />}
              label="Total Jobs"
              value={String(stats.totalJobs)}
              sub="completed records"
              enough={stats.totalJobs > 0}
            />
            <StatCard
              icon={<FileText size={14} />}
              label="Total Contract Value"
              value={money(stats.totalContractValue || 0, stats.currency)}
              // Currencies are never added together, so when an account bills in
              // more than one the others are named rather than folded in.
              sub={
                stats.currencyTotals.length > 1
                  ? `plus ${stats.currencyTotals
                      .slice(1)
                      .map((t) => money(t.value, t.currency))
                      .join(", ")}`
                  : "across all records"
              }
              enough={stats.totalContractValue !== null}
            />
            <StatCard
              icon={<Star size={14} />}
              label="Average Client Rating"
              value={stats.averageRating === null ? "" : `${stats.averageRating} / 5`}
              sub={`from ${stats.ratedJobs} rated job${stats.ratedJobs === 1 ? "" : "s"}`}
              enough={stats.averageRating !== null}
            />
            <StatCard
              icon={<Target size={14} />}
              label="Top Job Category"
              value={stats.topCategory?.name || ""}
              sub={stats.topCategory ? `${stats.topCategory.count} jobs` : ""}
              enough={!!stats.topCategory}
            />
          </div>

          {/* ── Table ─────────────────────────────────────────────────────── */}
          <div className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden shadow-sm">
            <div className="p-3 border-b border-outline-variant flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant" />
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Search title, description, category, skill or technology…"
                  className="w-full pl-8 pr-3 py-1.5 bg-surface-container-low border border-outline-variant rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`px-3 py-1.5 border rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                  activeFilterCount ? "border-primary text-primary bg-primary/5" : "border-outline-variant text-on-surface"
                }`}
              >
                <SlidersHorizontal size={14} />
                {activeFilterCount || "Filters"}
              </button>
              <label className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                <input
                  type="checkbox"
                  checked={filters.includeArchived}
                  onChange={(e) => setFilters({ includeArchived: e.target.checked })}
                />
                Show archived
              </label>
            </div>

            {showFilters && (
              <div className="p-3 border-b border-outline-variant bg-surface-container-low/40 grid grid-cols-2 lg:grid-cols-4 gap-3">
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Category</span>
                  <select value={filters.category} onChange={(e) => setFilters({ category: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none">
                    <option value="">All</option>
                    {options.categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Subcategory</span>
                  <select value={filters.subcategory} onChange={(e) => setFilters({ subcategory: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none">
                    <option value="">All</option>
                    {options.subcategories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Contract type</span>
                  <select value={filters.contractType} onChange={(e) => setFilters({ contractType: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none">
                    <option value="">All</option>
                    {options.contractTypes.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Skill / technology</span>
                  <select value={filters.skill} onChange={(e) => setFilters({ skill: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none">
                    <option value="">All</option>
                    {options.skills.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Min rating</span>
                  <select value={filters.rating} onChange={(e) => setFilters({ rating: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none">
                    <option value="">Any</option>
                    <option value="5">5</option>
                    <option value="4">4+</option>
                    <option value="3">3+</option>
                  </select>
                </label>
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Min value</span>
                  <input type="number" value={filters.minValue} onChange={(e) => setFilters({ minValue: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none" />
                </label>
                <label>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">Ended after</span>
                  <input type="date" value={filters.from} onChange={(e) => setFilters({ from: e.target.value })} className="w-full px-2 py-1.5 bg-surface-container-lowest border border-outline-variant rounded-lg text-sm outline-none" />
                </label>
                <div className="flex items-end">
                  <button onClick={() => { resetFilters(); setSearchDraft(""); }} className="px-3 py-1.5 rounded-lg border border-outline-variant text-sm font-semibold text-on-surface-variant">
                    Clear
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-container-low">
                  <tr>
                    {["Job Title", "Category", "Contract Value", "Duration", "Rating", "Work Delivered", "Samples", "Proposal", "Conversation", ""].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-on-surface-variant whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {isLoading ? (
                    <tr><td colSpan={10} className="px-3 py-10 text-center text-on-surface-variant">Loading…</td></tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-12 text-center">
                        <p className="text-sm font-semibold text-on-surface">No job records yet</p>
                        <p className="text-xs text-on-surface-variant mt-1">
                          Add this account's completed work so the AI has evidence to match against.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    records.map((r) => (
                      <tr key={r.id} className={`hover:bg-surface-container-low/50 ${r.isArchived ? "opacity-55" : ""}`}>
                        <td className="px-3 py-2.5 max-w-[260px]">
                          <InlineEdit value={r.jobTitle} width="w-56" onSave={(v) => patchField(r.id, "jobTitle", v)} />
                          {r.isArchived && <span className="ml-2 text-[10px] font-bold uppercase text-outline">Archived</span>}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-on-surface-variant">
                          <InlineEdit value={r.category} width="w-36" onSave={(v) => patchField(r.id, "category", v)} />
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap font-semibold">
                          <InlineEdit value={r.contractValue} type="number" prefix={r.currency === "USD" ? "$" : `${r.currency} `} width="w-24" onSave={(v) => patchField(r.id, "contractValue", v)} />
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-on-surface-variant">
                          <InlineEdit value={r.duration} width="w-24" onSave={(v) => patchField(r.id, "duration", v)} />
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {r.rating !== null && <Star size={12} className="text-amber-500 fill-amber-500" />}
                            <InlineEdit value={r.rating} type="number" width="w-14" onSave={(v) => patchField(r.id, "rating", v)} />
                          </span>
                        </td>
                        <td className="px-3 py-2.5 max-w-[220px] text-on-surface-variant text-xs truncate" title={r.deliveredSummary}>
                          {r.deliveredSummary || "—"}
                          {r.deliverableCount > 0 && <span className="ml-1 text-outline">({r.deliverableCount})</span>}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-center text-on-surface-variant">
                          {r.workSampleCount > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs"><Paperclip size={12} />{r.workSampleCount}</span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-center">
                          {r.hasProposal ? <FileText size={14} className="text-tertiary inline" /> : <span className="text-outline">—</span>}
                        </td>
                        {/* Whether a conversation exists AND whether it turned out to be
                            worth anything. A stored thread that was all logistics must not
                            look like evidence the record does not actually have. */}
                        <td className="px-3 py-2.5 whitespace-nowrap text-center">
                          {!r.hasConversation ? (
                            <span className="text-outline">—</span>
                          ) : r.conversationDensity === null ? (
                            <span
                              title={r.conversationMessages + " messages · not analysed yet"}
                              className="inline-flex items-center gap-1 text-xs text-on-surface-variant"
                            >
                              <MessageSquare size={13} /> {r.conversationMessages}
                            </span>
                          ) : (
                            <span
                              title={
                                r.conversationMessages +
                                " messages · " +
                                r.conversationDensity +
                                "% signal — " +
                                (r.conversationDensity >= 30
                                  ? "counts as evidence"
                                  : "mostly logistics, not counted")
                              }
                              className={`inline-flex items-center gap-1 text-xs font-semibold ${
                                r.conversationDensity >= 30 ? "text-tertiary" : "text-outline"
                              }`}
                            >
                              <MessageSquare size={13} /> {r.conversationDensity}%
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="flex items-center gap-1 justify-end">
                            {r.hasAi && (
                              <span title={r.aiStale ? "AI insight is out of date" : "AI insight generated"}>
                                <Sparkles size={13} className={r.aiStale ? "text-amber-600" : "text-tertiary"} />
                              </span>
                            )}
                            <button onClick={() => open(r.id)} title="View" className="p-1.5 text-on-surface-variant hover:text-primary"><Eye size={14} /></button>
                            <button onClick={() => open(r.id)} title="Edit" className="p-1.5 text-on-surface-variant hover:text-primary"><Pencil size={14} /></button>
                            {r.isArchived ? (
                              <button onClick={() => restoreRecord(r.id)} title="Restore" className="p-1.5 text-on-surface-variant hover:text-tertiary"><RotateCcw size={14} /></button>
                            ) : (
                              <button onClick={() => setConfirmArchive(r)} title="Archive" className="p-1.5 text-on-surface-variant hover:text-error"><Archive size={14} /></button>
                            )}
                            {isAdmin && r.isArchived && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Permanently delete "${r.jobTitle}"? This cannot be undone.`)) deleteRecord(r.id);
                                }}
                                title="Delete permanently"
                                className="p-1.5 text-on-surface-variant hover:text-error"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {records.length > 0 && (
              <div className="px-3 py-2 border-t border-outline-variant text-[11px] text-on-surface-variant">
                {total} record{total === 1 ? "" : "s"} shown
              </div>
            )}
          </div>
        </>
      )}

      {/* Archiving says what it actually does to the AI, not just "are you sure". */}
      {confirmArchive && (
        <div className="fixed inset-0 z-[95] bg-black/50 grid place-items-center p-4" onClick={() => setConfirmArchive(null)}>
          <div className="bg-surface-container-lowest rounded-xl p-5 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-on-surface mb-2">Archive this job record?</h3>
            <p className="text-sm text-on-surface-variant leading-relaxed mb-1">
              <b className="text-on-surface">{confirmArchive.jobTitle}</b> will stop being used as
              evidence when matching jobs or writing proposals for this account.
            </p>
            <p className="text-xs text-on-surface-variant leading-relaxed mb-4">
              The record itself is kept, so proposals that already cited it still make sense. You can
              restore it at any time.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmArchive(null)} className="px-4 py-2 rounded-lg border border-outline-variant text-sm font-semibold">
                Cancel
              </button>
              <button
                onClick={() => { archiveRecord(confirmArchive.id); setConfirmArchive(null); }}
                className="px-4 py-2 rounded-lg bg-error text-on-error text-sm font-semibold"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      <JobRecordModal
        open={modalOpen}
        recordId={editingId}
        onClose={() => { setModalOpen(false); setEditingId(null); }}
      />
      </div>
    </div>
  );
};

export default FreelancerJobRecordsPage;
